
import React from 'react';
import { Attendee } from '@/types/attendee';
import { CommandGroup, CommandItem, CommandEmpty } from '@/components/ui/command';
import { User, Shirt, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatTShirtSize } from '@/utils/formatUtils';
import { motion } from 'framer-motion';
import { removeVietnameseAccents } from '@/components/admin/table/tableUtils';

interface SearchResultsProps {
  results: Attendee[];
  searchQuery: string;
  onSelect: (attendee: Attendee) => void;
  isLoading?: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({ 
  results, 
  searchQuery, 
  onSelect,
  isLoading = false
}) => {
  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query || !text) return text;
    
    const normalizedText = removeVietnameseAccents(text.toLowerCase());
    const normalizedQuery = removeVietnameseAccents(query.toLowerCase());
    
    if (!normalizedText.includes(normalizedQuery)) return text;
    
    const index = normalizedText.indexOf(normalizedQuery);
    const before = text.substring(0, index);
    const match = text.substring(index, index + query.length);
    const after = text.substring(index + query.length);
    
    return (
      <>
        {before}
        <span className="bg-amber-100 text-amber-900 dark:bg-amber-700/60 dark:text-amber-100 font-medium">{match}</span>
        {after}
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="py-6 text-center text-slate-400">
        <motion.div
          animate={{ 
            opacity: [0.5, 1, 0.5],
            scale: [0.98, 1, 0.98]
          }}
          transition={{ 
            repeat: Infinity, 
            duration: 1.5 
          }}
        >
          Đang tìm kiếm...
        </motion.div>
      </div>
    );
  }

  if (searchQuery && results.length === 0) {
    return <CommandEmpty>Không tìm thấy kết quả nào</CommandEmpty>;
  }

  if (!searchQuery || results.length === 0) return null;

  return (
    <CommandGroup heading={`Kết quả (${results.length})`}>
      {results.map((attendee, index) => (
        <CommandItem
          key={attendee.code || index}
          value={attendee.name || ""}
          onSelect={() => onSelect(attendee)}
          className={cn(
            "flex items-center justify-between py-3 cursor-pointer",
            !attendee.attended ? "border-l-4 border-blue-500" : "border-l-4 border-green-500"
          )}
        >
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <User size={14} className="text-slate-500" />
              <span className="font-medium text-slate-800 dark:text-slate-200">
                {highlightMatch(attendee.name || "", searchQuery)}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {highlightMatch(attendee.code || "", searchQuery)}
                {attendee.phone && ` · ${highlightMatch(attendee.phone, searchQuery)}`}
              </span>
              {attendee.t_shirt_size && (
                <div className="flex items-center gap-0.5 ml-1 text-xs">
                  <Shirt size={12} className="text-amber-600 dark:text-amber-400" />
                  <span className="text-amber-700 dark:text-amber-400">
                    {formatTShirtSize(attendee.t_shirt_size)}
                  </span>
                </div>
              )}
            </div>
          </div>
          <span className={cn(
            "text-xs px-2.5 py-1 rounded-full flex items-center gap-1",
            attendee.attended 
              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400" 
              : "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
          )}>
            {attendee.attended && <CheckCircle size={12} className="text-green-600" />}
            {attendee.attended ? "Đã điểm danh" : "Check-in"}
          </span>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};

export default SearchResults;
